import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatPhoneRu } from '../../shared/lib/phone';
import { Button } from '../../shared/ui/Button';
import { GeneratedPasswordField } from '../../shared/ui/GeneratedPasswordField';
import { TextField, inputClass } from '../../shared/ui/TextField';
import { Card, ErrorText, PageHeader, Spinner } from '../../shared/ui/primitives';
import type { Role } from '../auth/api';
import { closePartnershipRequest, getPartnershipRequest } from '../partnership/api';
import { createUser } from './api';

const ROLES: Role[] = ['doctor', 'admin'];
const roleLabel: Record<string, string> = { admin: 'Администратор', doctor: 'Врач' };

interface FormValues {
  fullName: string;
  email: string;
  password: string;
  phone: string;
  role: Role;
  sendCredentials: boolean;
}

/** Регистрация отключена — учётные записи заводит только администратор. */
export function AdminUserCreatePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestId = params.get('requestId');
  const request = useQuery({
    queryKey: ['admin', 'partnership-request', requestId],
    queryFn: () => getPartnershipRequest(requestId!),
    enabled: Boolean(requestId),
    staleTime: Infinity,
  });
  const queryClient = useQueryClient();

  const { control, register, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: {
      fullName: '',
      email: '',
      password: '',
      phone: '',
      role: 'doctor',
      sendCredentials: true,
    },
  });
  useEffect(() => {
    if (request.data)
      reset({
        fullName: request.data.fullName,
        email: request.data.email,
        phone: request.data.phone,
        password: '',
        role: 'doctor',
        sendCredentials: true,
      });
  }, [request.data, reset]);
  const password = watch('password');
  const requestReady = !requestId || request.data?.status === 'new';
  const close = useMutation({
    mutationFn: () => closePartnershipRequest(requestId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      navigate('/admin/partnership?status=closed');
    },
  });

  const create = useMutation({
    mutationFn: (v: FormValues) =>
      createUser({ ...v, ...(requestId ? { partnershipRequestId: requestId } : {}) }),
    onSuccess: async (user) => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      // Статус письма покажет карточка пользователя (см. CredentialsEmailNotice).
      navigate(`/admin/doctors/${user.id}`, {
        replace: true,
        state: { credentialsEmail: user.credentialsEmail },
      });
    },
  });

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Link
          to={requestId ? '/admin/partnership' : '/admin/doctors'}
          className="text-sm font-medium text-accent hover:text-accent-600"
        >
          {requestId ? '← К заявкам' : '← К списку врачей'}
        </Link>
        <PageHeader title={requestId ? 'Пользователь из заявки' : 'Новая учётная запись'} />
      </div>

      {requestId && request.isLoading && <Spinner />}
      {requestId && request.isError && (
        <ErrorText>{apiErrorMessage(request.error, 'Не удалось открыть заявку')}</ErrorText>
      )}
      {request.data && (
        <Card className="max-w-xl space-y-2 p-4 text-sm">
          <p>{[request.data.clinic, request.data.city].filter(Boolean).join(' · ')}</p>
          {request.data.comment && <p className="whitespace-pre-line">{request.data.comment}</p>}
          {request.data.status === 'closed' && (
            <p>
              Заявка уже закрыта.
              {request.data.createdUserId && (
                <Link
                  className="ml-2 text-accent"
                  to={`/admin/doctors/${request.data.createdUserId}`}
                >
                  Открыть пользователя
                </Link>
              )}
            </p>
          )}
        </Card>
      )}
      <Card className="max-w-xl space-y-3 p-4 sm:p-5">
        <form
          onSubmit={handleSubmit((v) => {
            if (requestReady && !close.isPending && !create.isPending) create.mutate(v);
          })}
          className="grid gap-3 sm:grid-cols-2"
        >
          <fieldset
            className="contents"
            disabled={!requestReady || create.isPending || close.isPending}
          >
            <TextField label="ФИО" {...register('fullName')} />
            <TextField
              label="E-mail"
              type="email"
              autoComplete="email"
              required
              {...register('email')}
            />
            <div className="sm:col-span-2">
              <Controller
                name="password"
                control={control}
                render={({ field: f }) => (
                  <GeneratedPasswordField value={f.value} onChange={f.onChange} />
                )}
              />
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-ink/80">Телефон</span>
              <Controller
                name="phone"
                control={control}
                render={({ field: f }) => (
                  <input
                    className={inputClass}
                    inputMode="tel"
                    placeholder="+7(___)___-__-__"
                    value={f.value}
                    onChange={(e) => f.onChange(formatPhoneRu(e.target.value))}
                  />
                )}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-ink/80">Роль</span>
              <select className={inputClass} {...register('role')}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleLabel[r]}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-2 text-sm text-ink/80 sm:col-span-2">
              <input
                type="checkbox"
                className="size-4 accent-accent"
                {...register('sendCredentials')}
              />
              Отправить логин и пароль на e-mail
            </label>

            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
              <Button
                type="submit"
                disabled={
                  !requestReady || create.isPending || close.isPending || password.length < 8
                }
              >
                {create.isPending
                  ? 'Создание…'
                  : requestId
                    ? 'Сохранить пользователя и закрыть заявку'
                    : 'Создать'}
              </Button>
              {requestId && requestReady && (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={create.isPending || close.isPending}
                  onClick={() => close.mutate()}
                >
                  Закрыть без создания пользователя
                </Button>
              )}
              {close.isError && (
                <ErrorText>{apiErrorMessage(close.error, 'Не удалось закрыть заявку')}</ErrorText>
              )}
              {password.length > 0 && password.length < 8 && (
                <span className="text-sm text-muted">Пароль не короче 8 символов</span>
              )}
              {create.isError && (
                <span className="text-sm text-danger">
                  {apiErrorMessage(create.error, 'Не удалось создать учётную запись')}
                </span>
              )}
            </div>
          </fieldset>
        </form>
      </Card>
    </section>
  );
}
